/// <reference path="../types/littleg-my-tools-globals.d.ts" />
/* assets/littleg-my-tools-adapter.js — D01-MY-TOOLS-CORE-ADAPTER-03
 * Read-only transport for the canonical LittleG WebSession and D01-02 Core API.
 * Does not own token storage, entitlement decisions, purchase links or tool execution.
 */
(() => {
  'use strict';
  /**
   * @typedef {{ status?: string, profile?: { id?: string } }} LittleGSessionState
   * @typedef {{
   *   getState?: () => LittleGSessionState | null,
   *   authenticatedFetch?: (url: string, options: RequestInit) => Promise<Response>
   * }} LittleGSession
   * @typedef {{
   *   root: Element, panelId: string, requestId: string, subject: string,
   *   controller: AbortController, timer: number | null
   * }} MyToolsJob
   */
  const VERSION = 'D01-MY-TOOLS-CORE-ADAPTER-03';
  if (window.__LITTLEG_MY_TOOLS_ADAPTER__ === VERSION) return;
  window.__LITTLEG_MY_TOOLS_ADAPTER__ = VERSION;

  const API_URL = 'https://api.glhksolution.com/v1/commerce/my-tools';
  const CONTRACT = 'MY_TOOLS_READ_ONLY_V1';
  const TIMEOUT_MS = 8500; // Before the existing My Tools panel's 10s timeout.
  const MAX_TOOLS = 100;
  const TOOL_IDS = new Set([
    'bb-voice-growth-diary',
    'family-memory-book',
    'property-rights-verification-workflow'
  ]);
  /** @type {Readonly<Record<string,string>>} */
  const CONTINUE_URLS = Object.freeze({
    'property-rights-verification-workflow': '/collections/land-natural-resources?source=my-tools&intent=continue#land-property-rights-verification'
  });
  /** @type {Map<string, MyToolsJob>} */
  const pending = new Map();
  /** @type {Map<string, MyToolsJob>} */
  const pendingLinks = new Map();
  const LINK_ASSERTION_URL = '/apps/gcity-api/auth/mobile-session-assertion';
  const LINK_API_URL = 'https://api.glhksolution.com/v1/commerce/account-link';
  const LINK_AUDIENCE = 'gcity-core-api:mobile-session-exchange';
  const LINK_TIMEOUT_MS = 16000;

  const activeToolsTab = () => {
    try {
      return new URL(window.location.href).searchParams.get('tab') === 'tools';
    } catch (_) {
      // Malformed navigation state is treated as an inactive tab.
      return false;
    }
  };
  const sessionSubject = () => {
    try {
      const state = window.LittleGWebSession?.getState?.();
      return state?.status === 'authenticated' && typeof state.profile?.id === 'string'
        ? state.profile.id : '';
    } catch (_) {
      // If WebSession cannot be read, never infer an authenticated subject.
      return '';
    }
  };
  /** @param {MyToolsJob} job */
  const cancel = (job) => {
    if (pending.get(job.panelId) !== job) return;
    pending.delete(job.panelId);
    if (job.timer !== null) window.clearTimeout(job.timer);
    job.controller.abort();
  };
  /** @param {MyToolsJob} job */
  const current = (job) => pending.get(job.panelId) === job &&
    !job.controller.signal.aborted && activeToolsTab() &&
    sessionSubject() === job.subject &&
    document.getElementById(job.panelId) === job.root;

  /** @param {MyToolsJob} job */
  const currentLink = (job) => pendingLinks.get(job.panelId) === job &&
    !job.controller.signal.aborted && activeToolsTab() &&
    sessionSubject() === job.subject && document.getElementById(job.panelId) === job.root;
  /** @param {MyToolsJob} job */
  const cancelLink = (job) => {
    if (pendingLinks.get(job.panelId) !== job) return;
    pendingLinks.delete(job.panelId);
    if (job.timer !== null) window.clearTimeout(job.timer);
    job.controller.abort();
  };
  /** @param {MyToolsJob} job @param {boolean} linked @param {string} [errorCode] */
  const notifyLink = (job, linked, errorCode = '') => {
    if (!currentLink(job)) return;
    pendingLinks.delete(job.panelId);
    if (job.timer !== null) window.clearTimeout(job.timer);
    document.dispatchEvent(new CustomEvent('littleg:my-tools-link-data', {
      detail: { panelId: job.panelId, requestId: job.requestId, linked, errorCode }
    }));
  };
  /** @param {MyToolsJob} job @param {LittleGSession | undefined} session */
  const linkAccount = async (job, session) => {
    try {
      if (typeof session?.authenticatedFetch !== 'function') {
        notifyLink(job, false, 'my_tools_unavailable'); return;
      }
      // Same-origin Shopify App Proxy proves the commerce session; never send a client customer ID.
      const proofResponse = await fetch(LINK_ASSERTION_URL, {
        method: 'POST', credentials: 'same-origin', redirect: 'error',
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Request-ID': job.requestId },
        body: JSON.stringify({ audience: LINK_AUDIENCE }), signal: job.controller.signal
      });
      if (!currentLink(job)) return;
      if (!proofResponse.ok) {
        notifyLink(job, false, proofResponse.status === 401 ? 'shopify_session_required' : 'my_tools_unavailable'); return;
      }
      const proof = await proofResponse.json().catch(() => null);
      if (!currentLink(job)) return;
      const assertion = proof?.data?.assertion;
      if (proof?.success !== true || proof?.data?.audience !== LINK_AUDIENCE ||
          proof?.data?.shop !== 'littleg.group' || typeof assertion !== 'string' ||
          assertion.length > 4096 || !/^[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+\.[A-Za-z0-9_-]+$/.test(assertion)) {
        notifyLink(job, false, 'my_tools_unavailable'); return;
      }
      // Core validates one-time proof; account linking does not issue a product entitlement.
      const response = await session.authenticatedFetch(LINK_API_URL, {
        method: 'POST', signal: job.controller.signal,
        headers: { Accept: 'application/json', 'Content-Type': 'application/json', 'X-Request-ID': job.requestId },
        body: JSON.stringify({ shopify_assertion: assertion })
      });
      if (!currentLink(job)) return;
      const payload = await response.json().catch(() => null);
      if (!currentLink(job)) return;
      const linked = response.ok && payload?.success === true &&
        payload.request_id === job.requestId && payload.data?.status === 'linked' &&
        payload.data?.shop === 'littleg.group' && payload.data?.paid_access === 'not_granted_by_account_link';
      notifyLink(job, linked, linked ? '' : 'my_tools_unavailable');
    } catch (_) {
      // Network, proof and abort errors must not grant account linking.
      notifyLink(job, false, 'my_tools_unavailable');
    }
  };

  /** @param {MyToolsJob} job @param {Array<Record<string, unknown>> | null} tools @param {string} [errorCode] */
  const notify = (job, tools, errorCode = '') => {
    if (!current(job)) return;
    pending.delete(job.panelId);
    if (job.timer !== null) window.clearTimeout(job.timer);
    document.dispatchEvent(new CustomEvent('littleg:my-tools-data', {
      detail: { panelId: job.panelId, requestId: job.requestId, tools, errorCode }
    }));
  };
  /** @param {MyToolsJob} job */
  const timeout = (job) => {
    if (!current(job)) return;
    pending.delete(job.panelId);
    if (job.timer !== null) window.clearTimeout(job.timer);
    job.controller.abort();
    document.dispatchEvent(new CustomEvent('littleg:my-tools-data', {
      detail: {
        panelId: job.panelId, requestId: job.requestId,
        tools: null, errorCode: 'my_tools_unavailable'
      }
    }));
  };

  /** @param {any} item Core API JSON: validate before projecting any field. */
  const hasValidToolFields = item => {
    if (!item || !TOOL_IDS.has(item.toolId) ||
        typeof item.title !== 'string' || !item.title.trim() ||
        item.title.length > 120 || item.recentResult !== undefined) return false;
    const subscription = item.entitlementType === 'subscription';
    if (!subscription && item.entitlementType !== 'one_off' &&
        item.entitlementType !== 'review_tier') return false;
    const remaining = item.remainingUses;
    if (subscription ? remaining !== null :
        !Number.isSafeInteger(remaining) || remaining < 0) return false;
    if (item.validUntil !== null &&
        (typeof item.validUntil !== 'string' || item.validUntil.length > 64 ||
         !Number.isFinite(Date.parse(item.validUntil)))) return false;
    return true;
  };

  /**
   * @param {any} item Already validated tool; only the allowlisted route may survive.
   * @returns {{canContinue:boolean,continueUrl:string|null}|null}
   */
  const validatedContinue = item => {
    if (item.canContinue === false && item.continueUrl === null) {
      return { canContinue: false, continueUrl: null };
    }
    if (item.canContinue !== true) return null;
    const allowedContinueUrl = CONTINUE_URLS[item.toolId] || null;
    const legacyContinueUrl = item.toolId === 'property-rights-verification-workflow'
      ? '/products/' + item.toolId + '?source=my-tools&intent=continue' : null;
    if (!allowedContinueUrl ||
        (item.continueUrl !== allowedContinueUrl && item.continueUrl !== legacyContinueUrl) ||
        (item.entitlementType !== 'subscription' && item.remainingUses < 1)) return null;
    return { canContinue: true, continueUrl: allowedContinueUrl };
  };

  /** @param {any} payload Untrusted Core JSON, validated before display.
   *  @param {string} requestId
   *  @returns {Array<{title:string,remainingUses:number|null,canContinue:boolean,continueUrl:string|null}>|null}
   */
  const validatedTools = (payload, requestId) => {
    if (payload?.success !== true || payload.request_id !== requestId ||
        payload.meta?.contract !== CONTRACT || payload.meta?.authority !== 'core_d1' ||
        payload.meta?.fabricated_records !== false || !Array.isArray(payload.data?.tools) ||
        payload.data.tools.length > MAX_TOOLS) return null;
    const result = [];
    for (const item of payload.data.tools) {
      if (!hasValidToolFields(item)) return null;
      const continuation = validatedContinue(item);
      if (!continuation) return null;
      // Core authority stays on the server; never project order IDs or tokens.
      result.push({
        title: item.title.trim(), remainingUses: item.remainingUses,
        canContinue: continuation.canContinue,
        continueUrl: continuation.continueUrl
      });
    }
    return result;
  };

  /** @param {MyToolsJob} job @param {LittleGSession | undefined} session */
  const load = async (job, session) => {
    try {
      if (typeof session?.authenticatedFetch !== 'function') {
        notify(job, null, 'my_tools_unavailable');
        return;
      }
      const response = await session.authenticatedFetch(API_URL, {
        method: 'GET',
        headers: { Accept: 'application/json', 'X-Request-ID': job.requestId },
        signal: job.controller.signal
      });
      if (!current(job)) return;
      const payload = await response.json().catch(() => null);
      if (!current(job)) return;
      if (!response.ok) {
        const code = response.status === 403 &&
          payload?.error?.code === 'shopify_account_not_linked'
          ? 'shopify_account_not_linked' : 'my_tools_unavailable';
        notify(job, null, code);
        return;
      }
      const tools = validatedTools(payload, job.requestId);
      notify(job, tools, tools === null ? 'my_tools_unavailable' : '');
    } catch (_) {
      // Network, JSON and abort errors return an unavailable state, never tools.
      notify(job, null, 'my_tools_unavailable');
    }
  };

  document.addEventListener('littleg:my-tools-request', (event) => {
    const detail = /** @type {CustomEvent<{panelId:string,requestId:string}>} */ (event).detail;
    const target = /** @type {Element|null} */ (/** @type {unknown} */ (event.target));
    const root = target?.closest?.('[data-littleg-my-tools]');
    if (!root || !root.id || root.id !== detail?.panelId ||
        typeof detail.requestId !== 'string' ||
        !/^[A-Za-z0-9._:-]{1,96}$/.test(detail.requestId) ||
        !activeToolsTab()) return;
    const subject = sessionSubject();
    if (!subject) return;
    const previous = pending.get(root.id);
    if (previous) cancel(previous);
    /** @type {MyToolsJob} */
    const job = {
      root, panelId: root.id, requestId: detail.requestId, subject,
      controller: new AbortController(), timer: null
    };
    pending.set(root.id, job);
    job.timer = window.setTimeout(() => timeout(job), TIMEOUT_MS);
    void load(job, window.LittleGWebSession);
  });

  document.addEventListener('littleg:my-tools-link-request', (event) => {
    const detail = /** @type {CustomEvent<{panelId:string,requestId:string}>} */ (event).detail;
    const target = /** @type {Element|null} */ (/** @type {unknown} */ (event.target));
    const root = target?.closest?.('[data-littleg-my-tools]');
    if (!root || !root.id || root.id !== detail?.panelId || typeof detail.requestId !== 'string' ||
        !/^[A-Za-z0-9._:-]{1,96}$/.test(detail.requestId) || !activeToolsTab()) return;
    const subject = sessionSubject();
    if (!subject) return;
    const previous = pendingLinks.get(root.id);
    if (previous) cancelLink(previous);
    /** @type {MyToolsJob} */
    const job = { root, panelId: root.id, requestId: detail.requestId, subject,
      controller: new AbortController(), timer: null };
    pendingLinks.set(root.id, job);
    job.timer = window.setTimeout(() => {
      if (!currentLink(job)) return;
      notifyLink(job, false, 'my_tools_unavailable');
      job.controller.abort();
    }, LINK_TIMEOUT_MS);
    void linkAccount(job, window.LittleGWebSession);
  });

  const discardStale = () => {
    for (const job of pending.values()) {
      if (!current(job)) cancel(job);
    }
    for (const job of pendingLinks.values()) {
      if (!currentLink(job)) cancelLink(job);
    }
  };
  document.addEventListener('littleg:web-session-change', discardStale);
  window.addEventListener('popstate', discardStale);
  document.addEventListener('shopify:section:unload', (event) => {
    const target = /** @type {Element|null} */ (/** @type {unknown} */ (event.target));
    for (const job of pending.values()) {
      if (target?.contains?.(job.root) || target === job.root) cancel(job);
    }
    for (const job of pendingLinks.values()) {
      if (target?.contains?.(job.root) || target === job.root) cancelLink(job);
    }
  });
})();
